import { DiscrepancyKind } from "generated-graphql/graphql";

import { type DiscrepancyStatus, getDiscrepancyStatus } from "../DiscrepancyReviewDialog.helpers";

/**
 * The top-line discrepancy rows the section shows — a flat list, no class
 * grouping. The two former "compliance" types are now their own rows, and the
 * old "Field discrepancies" class becomes a single aggregate row (no per-field
 * breakdown):
 *  - `epaCorrection` — EPA correction requests.
 *  - `eManifestMissing` — a missing e-Manifest past the upload deadline.
 *  - `field` — every field-level disagreement, rolled up as one row.
 *  - `fieldIssue` — CE-driven single-record validations (e.g. suspect TX state waste codes).
 */
export type DiscrepancyRowKey = "epaCorrection" | "eManifestMissing" | "field" | "fieldIssue";

export const DISCREPANCY_ROW: Record<DiscrepancyRowKey, { label: string }> = {
  epaCorrection: { label: "EPA-Requested Corrections" },
  eManifestMissing: { label: "Missing e-Manifest" },
  // Labels match the Discrepancy Review sections these rows click through to.
  field: { label: "Data Source Conflicts" },
  fieldIssue: { label: "Data Quality Issues" },
};

/** Render order, top to bottom. */
const ROW_ORDER: DiscrepancyRowKey[] = ["epaCorrection", "eManifestMissing", "field", "fieldIssue"];

/**
 * Per-status tallies plus their sum. IGNORED rows are dismissed
 * non-discrepancies and are excluded from the breakdown entirely — they don't
 * get a slot and don't count toward `total`.
 */
export interface StatusCounts {
  unresolved: number;
  pending: number;
  resolved: number;
  total: number;
}

export interface DiscrepancyRow {
  key: DiscrepancyRowKey;
  label: string;
  counts: StatusCounts;
}

export interface DiscrepancyBreakdown {
  /** All four rows in {@link ROW_ORDER}; each may have a zero total. */
  rows: DiscrepancyRow[];
  /** Grand totals across all rows. */
  counts: StatusCounts;
  total: number;
}

/** The minimal discrepancy shape the breakdown needs. */
export type BreakdownInput = {
  field: string;
  kind?: string | null;
  status?: string | null;
};

const emptyCounts = (): StatusCounts => ({
  unresolved: 0,
  pending: 0,
  resolved: 0,
  total: 0,
});

function tally(counts: StatusCounts, status: Exclude<DiscrepancyStatus, "ignored">): void {
  counts[status] += 1;
  counts.total += 1;
}

function classify(d: BreakdownInput): DiscrepancyRowKey {
  if (d.kind === DiscrepancyKind.EpaCorrectionRequest) return "epaCorrection";
  if (d.kind === DiscrepancyKind.EmanifestMissing) return "eManifestMissing";
  if (d.kind === DiscrepancyKind.FieldIssue) return "fieldIssue";
  // Everything else is a field-level disagreement (the Encamp-flagged bucket).
  return "field";
}

/**
 * Open (unresolved + pending) counts split by kind — the shape the two-bubble
 * Discrepancies display consumes. Mirrors the backend `ManifestDiscrepancyTally`
 * so client-computed (detail summary) and server-provided (list / line-item)
 * tallies render through the same component.
 */
export interface DiscrepancyTallyView {
  complianceTotal: number;
  missingEManifest: number;
  epaCorrection: number;
  /** Open EPA correction preview lines from the server tally (list); empty when client-built. */
  epaCorrectionPreviewLines: string[];
  fieldTotal: number;
  fieldUnresolved: number;
  fieldPending: number;
  /** Resolved field discrepancies (the "done" slot; not in fieldTotal). */
  fieldResolved: number;
}

/**
 * Derive the discrepancy tally from raw discrepancy rows (used by the detail
 * summary, which already has the rows loaded). Compliance counts stay open-only;
 * the field bucket additionally surfaces resolved rows as `fieldResolved` so the
 * field bubble can show the unresolved / pending / resolved breakdown. Field
 * issues (data quality) fold into the field bucket, mirroring the server tally
 * — viewer scoping is the caller's job (the customer detail branch never passes
 * FIELD_ISSUE rows).
 */
export function buildDiscrepancyTally(
  discrepancies: readonly BreakdownInput[]
): DiscrepancyTallyView {
  const { rows } = buildDiscrepancyBreakdown(discrepancies);
  const bucket = (key: DiscrepancyRowKey) => rows.find((r) => r.key === key)?.counts;
  const open = (key: DiscrepancyRowKey) => {
    const counts = bucket(key);
    return { unresolved: counts?.unresolved ?? 0, pending: counts?.pending ?? 0 };
  };
  const missing = open("eManifestMissing");
  const epa = open("epaCorrection");
  const field = open("field");
  const fieldIssue = open("fieldIssue");
  const missingEManifest = missing.unresolved + missing.pending;
  const epaCorrection = epa.unresolved + epa.pending;
  const fieldUnresolved = field.unresolved + fieldIssue.unresolved;
  const fieldPending = field.pending + fieldIssue.pending;
  return {
    missingEManifest,
    epaCorrection,
    epaCorrectionPreviewLines: [],
    complianceTotal: missingEManifest + epaCorrection,
    fieldUnresolved,
    fieldPending,
    fieldTotal: fieldUnresolved + fieldPending,
    fieldResolved: (bucket("field")?.resolved ?? 0) + (bucket("fieldIssue")?.resolved ?? 0),
  };
}

/**
 * Roll discrepancy rows up into the flat top-line rows the Discrepancies
 * section renders. Pure; pass the already flag-filtered rows (e.g.
 * EPA-correction rows removed when that flag is off). IGNORED rows are
 * dismissed non-discrepancies and are skipped — callers don't need to
 * pre-filter them.
 */
export function buildDiscrepancyBreakdown(
  discrepancies: readonly BreakdownInput[]
): DiscrepancyBreakdown {
  const buckets: Record<DiscrepancyRowKey, StatusCounts> = {
    epaCorrection: emptyCounts(),
    eManifestMissing: emptyCounts(),
    field: emptyCounts(),
    fieldIssue: emptyCounts(),
  };
  const grand = emptyCounts();

  for (const d of discrepancies) {
    const status = getDiscrepancyStatus(d);
    if (status === "ignored") continue;
    tally(buckets[classify(d)], status);
    tally(grand, status);
  }

  return {
    rows: ROW_ORDER.map((key) => ({
      key,
      label: DISCREPANCY_ROW[key].label,
      counts: buckets[key],
    })),
    counts: grand,
    total: grand.total,
  };
}
